
import * as React from 'react';
import * as PropTypes from 'prop-types'

import * as ct from '../../../global/controls';

import { Product, ProductPricingMode, ProductType } from '../../../../store/pages/products/types';
import * as productsApi from '../../../../store/pages/products/productsApi'
import { ProductCategory } from '../../../../store/pages/productCategories/types';
import { TaxRate } from '../../../../store/pages/taxRates/types';
import Loading from '../../../global/loading';
import { clickHandler, isNullOrEmpty } from '../../../../utils/util';
import { ActivityFormat } from '../../../../store/pages/activityFormats/types';
import { Link } from 'react-router-dom';
import { ProductTaxMode, Venue } from '../../../../store/pages/venues/types';
import * as v from "../../../global/validation";
import { hasStockConfiguration } from "../../../../store/pages/products/helpers";


interface ProductListProps {
    venue: Venue;
    products: Product[];
    categories: ProductCategory[];
    activityFormats: ActivityFormat[];
    taxRates: TaxRate[];
    isLoadingProducts: boolean;
    isLoadingProductCategories: boolean;
    isLoadingTaxRates: boolean;
    editProduct: () => void;
    showModal: (overlayComponent: JSX.Element, screenName: string, noScroll?: boolean) => void;
}

enum FilterIncludeType {
    All = 0,
    Activities,
    NonActivities
}

interface ProductListState {
    showArchived: boolean;
    filter: string;
    filterIncludeType: ct.FormValue<FilterIncludeType>;
    exporting: boolean
}

class ProductList extends React.Component<ProductListProps, ProductListState> {

    constructor(props: ProductListProps) {
        super(props);

        this.state = { showArchived: false, filter: '', filterIncludeType: ct.asFormValue('filterIncludeType', FilterIncludeType.All), exporting: false }
    }

    static contextTypes = {
        t: PropTypes.func
    }
    
    isLoading = () => this.props.isLoadingProducts || this.props.isLoadingProductCategories || this.props.isLoadingTaxRates;

    validateFilterIncludeType = (val: FilterIncludeType) => v.validate(val, 'filterIncludeType', [], []);
    
    selectedFilterIncludeTypeChanged = (val: string) => {
        const et = parseInt(val);
        const selectedValue = isNaN(et) || et < 0 ? 0 : et;
        this.setState({ filterIncludeType: this.validateFilterIncludeType(selectedValue) });
    }

    filterProductsByIncludeType = (p: Product, filterIncludeType: FilterIncludeType) => {
        switch (filterIncludeType) {
            case FilterIncludeType.All:
                return true;
            case FilterIncludeType.Activities:
                return p.isActivityProduct;
            case FilterIncludeType.NonActivities:
                return !p.isActivityProduct;
            default:
                return false;
        }
    }

    exportProducts = () => {
        const { venue } = this.props;
        const reportName = this.context.t('ProductList:productsCsvReportName');

        this.setState({ exporting: true }, () => productsApi.exportProducts(venue.id, reportName, (success: boolean, error: string | null) => this.setState({ exporting: false })));
    }

    render() {
        const { venue } = this.props;
        const { showArchived, filter, filterIncludeType, exporting } = this.state;
        const { t } = this.context;
        const body = this.isLoading() ? <Loading /> : this.renderProductList();

        const filterIncludeTypeOptions = [{ key: '0', name: t('ProductList:filterIncludeTypeAll') }].concat([FilterIncludeType.Activities, FilterIncludeType.NonActivities].map(k => ({ key: k.toString(), name: t(`ProductList:filterIncludeType${FilterIncludeType[k]}`) })));
        
        return <section className='venue-item-list'>
            <header className='section-header'>
                <div className='section-actions' style={{ justifyContent: 'space-between' }}>
                    <Link to={`/venue/${venue.id}/product/new`} className='btn btn-info'>{this.context.t('ProductList:addProduct')}</Link>
                    <div className='section-header-filters'>
                        <label>
                            <input type='checkbox' checked={showArchived} onChange={e => this.setState({ showArchived: e.currentTarget.checked })} />
                            <span style={({ marginLeft: '10px' })}>{t('Global:showArchived')}</span>
                        </label>
                        {/*<button className='btn btn-info' onClick={e => clickHandler(e, this.addProduct)}>{t('ProductList:addProduct')}</button>*/}
                        <div className='pull-right' style={{width: '50%'}}>
                            <div className='row row-no-gutters'>
                                <div className='col-xs-6'>
                                    <ct.TextBox style={{ display: 'inline-block', marginLeft: '8px' }} id='filter' labelKey='Global:filter' value={ct.asFormValue('filter', filter)} callback={val => this.setState({ filter: val })} />
                                </div>
                                <div className='col-xs-6'>
                                    <ct.Select id='filterIncludeType' labelKey='ProductList:filterIncludeType' value={({ ...filterIncludeType, value: filterIncludeType.value ? filterIncludeType.value.toString() : '0' })} callback={this.selectedFilterIncludeTypeChanged} options={filterIncludeTypeOptions} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <button className='btn btn-primary' onClick={e => clickHandler(e, this.exportProducts)} disabled={exporting}>{t('Global:export')}</button>
                </div>
            </header>
            <div className='at-panel sub-section-panel-240px'>
                {body}
            </div>
        </section>;
    }
    
    private renderProductList = () => {
        const { t } = this.context;
        const { showArchived, filter, filterIncludeType } = this.state;
        const { venue, products, categories } = this.props;
        const lowerFilter = filter.toLowerCase();

        const warningIconStyle = { color: '#f9b745', marginRight: '6px' }

        const filteredProducts = products
            .filter(p => p.type === ProductType.Standard && p.venueId === venue.id && (showArchived || !p.archived) && (isNullOrEmpty(filter) || p.name.toLowerCase().includes(lowerFilter)) && this.filterProductsByIncludeType(p, filterIncludeType.value))
            .map(p => {
                const warnings: string[] = [];
                
                if (p.isActivityProduct && hasStockConfiguration(p)) {
                    warnings.push(t('ProductList:stockConfigurationDisabledWarning'));
                }

                const basePrice = p.pricing.filter(p => p.effectiveFrom <= new Date() && (p.effectiveTo == null || p.effectiveTo >= new Date()) && p.applyPriceOnDays === 0)[0]
            return (
                <tr key={p.id}>
                    <td><Link to={`/venue/${venue.id}/product/${p.id}`} className='btn btn-link btn-no-padding'>{isNullOrEmpty(p.name) ? <span>[{t('Global:edit')}]</span> : p.name}</Link></td>
                    {showArchived ? <td>{p.archived ? <span className='label label-danger'>{t('Global:archived')}</span> : null}</td> : null}
                    <td>{basePrice ? p.pricingMode === ProductPricingMode.Fixed ? basePrice.fixedPricing.map(p => p.price.toFixed(2))[0] : basePrice.unitPrice.toFixed(2) : null}</td>
                    <td><span style={{ fontSize: 'smaller', color: '#444', whiteSpace: 'nowrap' }}>{t(venue.productTaxMode === ProductTaxMode.IncludingTax ? 'ProductList:IncTax' : 'ProductList:excludingTax')}</span></td>
                    <td>{t(`ProductPricingMode:${ProductPricingMode[p.pricingMode]}`)}</td>
                    <td>{p.taxRateName}</td>
                    <td><ul className='list-unstyled list-no-margin'>{categories.filter(c => c.venueId === venue.id && p.categoryIds.indexOf(c.id) >= 0).map(c => <li key={c.id} style={this.buildCategoryStyle(c)}>{c.name}</li>)}</ul></td>
                    <td>{p.nominalCode}</td>
                    <td>{p.accountingCategory}</td>
                    <td>{p.accountingDepartment}</td>
                    <td>{warnings.map(msg => <div><span className='glyphicon glyphicon-alert' style={warningIconStyle}></span><span>{msg}</span></div>)}</td>
                </tr>
            );
        });

        return (
            <table className='table table-condensed'>
                <thead>
                    <tr>
                        <th>{this.context.t('ProductList:nameHeading')}</th>
                        {showArchived ? <th>{t('Global:archived')}</th> : null}
                        <th colSpan={2}>{this.context.t('Global:price')}</th>
                        <th>{this.context.t('ProductList:pricingModeHeading')}</th>
                        <th>{this.context.t('ProductList:taxRateHeading')}</th>
                        <th>{this.context.t('ProductList:categoryHeading')}</th>
                        <th>{this.context.t('ProductList:nominalCodeHeading')}</th>
                        <th>{this.context.t('ProductList:accountingCategoryHeading')}</th>
                        <th>{this.context.t('ProductList:accountingDepartmentHeading')}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredProducts}
                </tbody>
            </table>
        );
    }

    buildCategoryStyle(category: ProductCategory) {
        return { display: 'inline-block', marginLeft: '5px', borderLeft: `4px solid ${category.colour}`, paddingLeft: '2px' };
    }

}

// Wire up the React component to the Redux store
export default ProductList;
